// src/hooks/useCampaignIntel.js
import { useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import { normalizeSectorName } from "../utils/normalizeSectorName";

export default function useCampaignIntel(authId, toast) {
  const [intel, setIntel] = useState({});
  const [loadingIntel, setLoadingIntel] = useState(true);

  useEffect(() => {
    if (!authId) return;

    const fetchIntel = async () => {
      setLoadingIntel(true);

      try {
        // 1️⃣ Load static household counts
        const { data: sectors, error: sectorsError } = await supabase
          .from("postcode_sectors")
          .select("postcode_sector, household_count");

        if (sectorsError) throw sectorsError;

        const map = {};

        sectors.forEach((row) => {
          const key = normalizeSectorName(row.postcode_sector);
          if (!key) return;

          map[key] = {
            households: row.household_count ?? null,
            slots_sold: 0,
            slots_available: null,
            roi_estimate: null,
            velocity: null,
            saturation: null,
          };
        });

        // 2️⃣ Load dynamic intel via RPC (single call)
        const { data: intelRows, error: intelError } = await supabase.rpc(
          "update_campaign_intelligence",
          { p_auth_id: authId }
        );

        if (intelError) throw intelError;

        intelRows.forEach((row) => {
          const key = normalizeSectorName(row.postcode_sector);
          if (!key) return;

          if (!map[key]) {
            map[key] = {
              households: null,
              slots_sold: 0,
              slots_available: null,
              roi_estimate: null,
              velocity: null,
              saturation: null,
            };
          }

          map[key].households = row.households ?? map[key].households;
          map[key].slots_sold = row.slots_sold ?? map[key].slots_sold;
          map[key].slots_available = row.slots_available ?? map[key].slots_available;
          map[key].roi_estimate = row.roi_estimate ?? map[key].roi_estimate;
          map[key].velocity = row.redemption_velocity_7d ?? map[key].velocity;
          map[key].saturation = row.saturation ?? map[key].saturation;
        });

        // 3️⃣ Sort keys for stable UI
        const sorted = Object.fromEntries(
          Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
        );

        setIntel(sorted);
      } catch (err) {
        console.error("❌ Failed to load campaign intel:", err);
        toast?.({
          title: "Failed to load sector intelligence",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingIntel(false);
      }
    };

    fetchIntel();
  }, [authId, toast]);

  return { intel, loadingIntel };
}
